import { useState } from 'react';

const MONTH_NAMES = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function isPast(year: number, month: number, day: number) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(year, month, day);
  return date < today;
}

function isWeekend(year: number, month: number, day: number) {
  const date = new Date(year, month, day);
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

function isToday(year: number, month: number, day: number) {
  const today = new Date();
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
}

function formatDateString(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isSameDay(d1: string, year: number, month: number, day: number) {
  return d1 === formatDateString(year, month, day);
}

interface CalendarPickerProps {
  selectedDate: string;
  onSelect: (date: string) => void;
  availability?: Record<string, { isOpen: boolean; hasAvailableSlots: boolean; isNationalHoliday?: boolean; note?: string | null }>;
  loading?: boolean;
}

export default function CalendarPicker({ selectedDate, onSelect, availability = {}, loading = false }: CalendarPickerProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) {
    currentWeek.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const dayLabels = ['日', '一', '二', '三', '四', '五', '六'];

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="min-h-[44px] min-w-[44px] w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#FAF8F5] transition-colors text-[#1B3D2A]"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <div className="text-base font-bold text-[#1B3D2A]">
          {viewYear} 年 {MONTH_NAMES[viewMonth]}
        </div>
        <button
          type="button"
          onClick={handleNextMonth}
          className="min-h-[44px] min-w-[44px] w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#FAF8F5] transition-colors text-[#1B3D2A]"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayLabels.map((label) => (
          <div key={label} className="text-center text-sm font-medium text-[#9A8A7A] py-2">
            {label}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 gap-1">
        {weeks.map((week, wi) =>
          week.map((day, di) => {
            const key = `${wi}-${di}`;
            if (day === null) {
              return <div key={key} className="min-h-[44px] h-10" />;
            }

            const past = isPast(viewYear, viewMonth, day);
            const isTodayDate = isToday(viewYear, viewMonth, day);
            const selected = isSameDay(selectedDate, viewYear, viewMonth, day);
            const dateString = formatDateString(viewYear, viewMonth, day);
            const availabilityItem = availability[dateString];
            const disabled = loading || past || !availabilityItem?.isOpen || !availabilityItem?.hasAvailableSlots;

            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                title={availabilityItem?.note || undefined}
                onClick={() => onSelect(dateString)}
                className={`min-h-[44px] h-10 w-full rounded-lg text-sm font-medium transition-all duration-200 relative
                  ${selected
                    ? 'bg-[#1B3D2A] text-white shadow-md'
                    : disabled
                      ? 'text-[#D8D0C6] cursor-not-allowed bg-white/40'
                      : availabilityItem?.isNationalHoliday
                        ? 'bg-[#FFF4E5] text-[#8A5B14] hover:bg-[#FFE8C6]'
                        : isTodayDate
                          ? 'bg-[#D4A574]/20 text-[#1B3D2A] hover:bg-[#D4A574]/30'
                          : 'text-[#1B3D2A] hover:bg-[#FAF8F5]'
                  }`}
              >
                {day}
                {isTodayDate && !selected && !disabled && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#D4A574]" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs text-[#9A8A7A]">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#1B3D2A]" />
          <span>已選</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[#D4A574]/20" />
          <span>今天</span>
        </div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#FFF4E5]" /><span>特殊日</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-white border border-[#E8E0D6]" /><span>不可預約</span></div>
      </div>
    </div>
  );
}
